import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// Route configuration mapping paths to labels
const routeConfigs = {
  '/': { label: 'Home' },
  '/admin': { label: 'Dashboard' },
  '/my-account': { label: 'My Account' },
  '/users': { label: 'Users' },
  '/users/add': { label: 'Add User', parent: '/users' },
  '/cards': { label: 'Cards' },
  '/cards/add': { label: 'Add Card', parent: '/cards' },
  '/companies': { label: 'Companies' },
  '/companies/add': { label: 'Add Company', parent: '/companies' },
  '/designers': { label: 'Designers' },
  '/designers/add': { label: 'Add Designer', parent: '/designers' },
  '/obituaries': { label: 'Obituaries' },
  '/branches': { label: 'Branches' },
};

/**
 * Hook to generate breadcrumbs based on current route
 * @param {Object} customData - Optional object with dynamic data (e.g., { companyName, branchName })
 */
const useBreadcrumbs = (customData = {}) => {
  const location = useLocation();
  const params = useParams();

  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    const items = [];

    // Always start with Home
    items.push({ label: 'Home', href: '/' });

    // Split path into segments
    const segments = pathname.split('/').filter(Boolean);

    // Build breadcrumb chain
    let currentPath = '';
    
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      
      // Handle dynamic routes (UUIDs)
      if (segment.match(/^[a-f0-9-]{36}$/i)) {
        // It's a UUID - check if we have custom data for it
        let label = segment;
        
        // Determine the label based on position and custom data
        if (index === 1 && segments[0] === 'companies' && customData.companyName) {
          label = customData.companyName;
        } else if (index === 1 && segments[0] === 'branches' && customData.branchName) {
          label = customData.branchName;
        } else if (index === 1 && segments[0] === 'obituaries' && customData.obituaryName) {
          label = customData.obituaryName;
        } else if (index === 1 && segments[0] === 'users' && customData.userName) {
          label = customData.userName;
        } else if (index === 1 && segments[0] === 'cards' && customData.cardName) {
          label = customData.cardName;
        } else if (index === 1 && segments[0] === 'designers' && customData.designerName) {
          label = customData.designerName;
        } else if (index === 3 && segments[0] === 'companies' && segments[2] === 'branches' && customData.branchName) {
          label = customData.branchName;
        } else if (index === 5 && segments[0] === 'companies' && segments[2] === 'branches' && segments[4] === 'obituaries' && customData.obituaryName) {
          label = customData.obituaryName;
        }
        
        items.push({
          label,
          href: isLast ? null : currentPath,
        });
      } else {
        // Static route segment
        const config = routeConfigs[currentPath];
        
        if (config) {
          items.push({
            label: config.label,
            href: isLast ? null : currentPath,
          });
        } else {
          // Handle nested routes
          if (currentPath.includes('/companies') && segment === 'branches') {
            items.push({
              label: 'Branches',
              href: isLast ? null : currentPath,
            });
          } else if (currentPath.includes('/branches') && segment === 'obituaries') {
            items.push({
              label: 'Obituaries',
              href: isLast ? null : currentPath,
            });
          } else if (segment !== 'branches' && segment !== 'obituaries') {
            // Only add if it's not a nested segment we already handled
            const capitalized = segment.charAt(0).toUpperCase() + segment.slice(1);
            items.push({
              label: capitalized,
              href: isLast ? null : currentPath,
            });
          }
        }
      }
    });

    return items;
  }, [location.pathname, customData]);

  return {
    breadcrumbs,
    currentTitle: breadcrumbs[breadcrumbs.length - 1]?.label || 'Page',
  };
};

export default useBreadcrumbs;

